// Correlation-ID generator. Every backend request gets a fresh request id
// that survives the Kafka envelope as `meta.correlationId`. See Epic
// FE-US-INT-01 §4.3.
//
// We prefer `crypto.randomUUID()` (RFC 4122 v4) when available. In test
// environments where the global is stubbed we fall back to a tiny v4
// generator that uses `crypto.getRandomValues` (also widely available in
// jsdom). Last-resort fallback is `Math.random` — only relevant in exotic
// environments where neither API exists. The output is always a v4 UUID
// shape, never something else, so backends can validate the header format.

const HEX = '0123456789abcdef';

function setUuidV4Bits(bytes: Uint8Array): void {
  // Set the version (4) and variant (10x) bits per RFC 4122 §4.4. The
  // explicit `?? 0` keeps TS strict happy — every index is in-bounds at
  // runtime so it's a no-op cast.
  // eslint-disable-next-line no-bitwise -- canonical UUID v4 encoding requires bitwise.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  // eslint-disable-next-line no-bitwise -- canonical UUID v4 encoding requires bitwise.
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
}

function fromGetRandomValues(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  setUuidV4Bits(bytes);
  return formatUuid(bytes);
}

function fromMathRandom(): string {
  // Final fallback — UUID-shaped but lower entropy. Acceptable: we only
  // ever hit this branch in environments without WebCrypto.
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  setUuidV4Bits(bytes);
  return formatUuid(bytes);
}

function formatUuid(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < 16; i += 1) {
    const b = bytes[i] ?? 0;
    // eslint-disable-next-line no-bitwise -- byte → 2 hex chars.
    out += (HEX[(b >>> 4) & 0xf] ?? '0') + (HEX[b & 0xf] ?? '0');
    if (i === 3 || i === 5 || i === 7 || i === 9) out += '-';
  }
  return out;
}

/** Generates a fresh request id (UUID v4). */
export function generateRequestId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  if (c?.getRandomValues) return fromGetRandomValues();
  return fromMathRandom();
}
